import { BitkitEmptyState, IconBuild } from '@bitrise/bitkit-v2';
import { useLayoutEffect, useMemo } from 'react';

import { DeployStepOutdatedAlert } from '@/components/test-results/DeployStepOutdatedAlert/DeployStepOutdatedAlert';
import { TestAddonNotAvailableEmptyState } from '@/components/test-results/TestAddonNotAvailableEmptyState/TestAddonNotAvailableEmptyState';
import { TestDataErrorState } from '@/components/test-results/TestDataErrorState/TestDataErrorState';
import {
  replaceTestResultsTrackingFields,
  trackTestsTabDisplayed,
} from '@/components/test-results/TestResults.tracking';
import TestResultsExplorer from '@/components/test-results/TestResultsExplorer/TestResultsExplorer';
import useBuildDetailsPageStore from '@/pages/BuildDetailsPage/BuildDetailsPage.store';

import type { BuildTestResults as TestResultsResponse } from '../BuildDetailsTabs/hooks/useBuildTestResults';
import { removeSingleTopLevelSuiteIfNameIs } from './BuildTestResults.utils';

type BuildTestResultsProps = {
  appSlug: string;
  isLoading: boolean;
  upgradePlan: boolean;
  artifactsUrl?: string;
  upgradePlanUrl: string;
  updateDeployStep: boolean;
  updateDeployStepUrl?: string;
  testResults?: TestResultsResponse;
  testResultsError?: string;
};

const BuildTestResults = ({
  appSlug,
  artifactsUrl,
  isLoading,
  testResults,
  testResultsError,
  updateDeployStep,
  updateDeployStepUrl,
  upgradePlan,
  upgradePlanUrl,
}: BuildTestResultsProps) => {
  const build = useBuildDetailsPageStore((state) => state.build);

  const testExplorerData = useMemo(() => {
    return removeSingleTopLevelSuiteIfNameIs(build.triggeredWorkflow, { testSuites: testResults?.testSuites ?? [] });
  }, [testResults, build.triggeredWorkflow]);

  useLayoutEffect(() => {
    if (isLoading) {
      return;
    }

    replaceTestResultsTrackingFields({
      projectId: appSlug,
      buildSlug: build.slug,
      buildStatus: build.status,
    });

    if (upgradePlan) {
      trackTestsTabDisplayed({ tabState: 'upgrade_plan' });
    } else if (updateDeployStep) {
      trackTestsTabDisplayed({ tabState: 'update_deploy_step' });
    } else if (testResultsError) {
      trackTestsTabDisplayed({ tabState: 'error' });
    } else {
      // NOTE: you can find tracking call in TestResultsExplorer because it tracks different status filter states
    }
  }, [appSlug, isLoading, build.slug, upgradePlan, build.status, updateDeployStep, testResultsError]);

  if (upgradePlan) {
    return <TestAddonNotAvailableEmptyState artifactsTabUrl={artifactsUrl} planSelectorUrl={upgradePlanUrl} />;
  }

  if (isLoading) {
    return (
      <BitkitEmptyState
        bodyText="Your test results will appear here"
        icon={IconBuild}
        loading
        headingText="This build is still running."
      />
    );
  }

  if (updateDeployStep) {
    return <DeployStepOutdatedAlert workflowEditorUrl={updateDeployStepUrl} />;
  }

  if (testResultsError) {
    return <TestDataErrorState errorMessage={testResultsError} />;
  }

  return (
    <TestResultsExplorer
      report={testExplorerData}
      projectSlug={appSlug}
      runnableType="build"
      runnableSlug={build.slug}
      runnableNumber={build.buildNumber}
    />
  );
};

export default BuildTestResults;
